import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui';
import { Button, Card, Badge, Input, Modal, Spinner } from '../../components/ui';
import { 
  Plus, Users, Trash2, Upload, Phone, CheckCircle, 
  FileText, Search, Download, Filter, X,
  UserPlus, ChevronRight, AlertCircle, Clock
} from 'lucide-react';
import api from '../../api/client';
import './Phonebooks.css';

const Phonebooks = () => {
  const [phonebooks, setPhonebooks] = useState([]);
  const [selectedPhonebook, setSelectedPhonebook] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({ name: '', description: '' });
  
  // variables 1-5
  const [addForm, setAddForm] = useState({ 
    phoneNumber: '', 
    variable1: '', 
    variable2: '', 
    variable3: '', 
    variable4: '', 
    variable5: '' 
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const { request } = useApi();
  const { success, error: toastError } = useToast();

  useEffect(() => {
    fetchPhonebooks();
  }, []);

  const fetchPhonebooks = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/phonebooks');
      setPhonebooks(data || []);
    } catch (err) {
      console.error('Failed to fetch phonebooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async (phonebookId) => {
    try {
      const data = await request('get', `/api/phonebooks/${phonebookId}/entries`);
      setEntries(data || []);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    }
  };

  const handleSelectPhonebook = (phonebook) => {
    setSelectedPhonebook(phonebook);
    fetchEntries(phonebook.id);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await request('post', '/api/phonebooks', form);
      setShowCreateModal(false);
      setForm({ name: '', description: '' });
      fetchPhonebooks();
      success('Phonebook created successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create phonebook');
      toastError(err.response?.data?.error || 'Failed to create phonebook');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNumber = async (e) => {
    e.preventDefault();
    if (!selectedPhonebook) return;
    setSubmitting(true);
    setError('');
    try {
      await request('post', `/api/phonebooks/${selectedPhonebook.id}/numbers`, {
        numbers: [{
          phoneNumber: addForm.phoneNumber,
          variable1: addForm.variable1,
          variable2: addForm.variable2,
          variable3: addForm.variable3,
          variable4: addForm.variable4,
          variable5: addForm.variable5,
          optedIn: true
        }]
      });
      
      // ✅ Reset form
      setAddForm({ 
        phoneNumber: '', 
        variable1: '', 
        variable2: '', 
        variable3: '', 
        variable4: '', 
        variable5: '' 
      });
      
      fetchEntries(selectedPhonebook.id);
      fetchPhonebooks();
      setShowAddModal(false);
      success('Contact added successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add number');
      toastError(err.response?.data?.error || 'Failed to add number');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadCsv = async () => {
    if (!uploadFile || !selectedPhonebook) return;
    
    setUploading(true);
    setError('');
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    try {
      const response = await api.post(`/api/phonebooks/${selectedPhonebook.id}/upload`, formData);
      
      setUploadResult(`✅ Successfully imported contacts!`);
      fetchEntries(selectedPhonebook.id);
      fetchPhonebooks();
      setUploadFile(null);
      success('Contacts imported successfully!');
      
      setTimeout(() => {
        handleCloseUploadModal();
      }, 2000);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.message || 'Upload failed. Please check file format.');
      toastError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadResult(null);
    setError('');
  };

  const handleDeletePhonebook = async (phonebookId) => {
    if (!window.confirm('Delete this phonebook? All contacts will be lost.')) return;
    try {
      await request('delete', `/api/phonebooks/${phonebookId}`);
      if (selectedPhonebook?.id === phonebookId) {
        setSelectedPhonebook(null);
        setEntries([]);
      }
      fetchPhonebooks();
      success('Phonebook deleted successfully!');
    } catch (err) {
      console.error('Failed to delete phonebook:', err);
      toastError('Failed to delete phonebook');
    }
  };

  const filteredPhonebooks = phonebooks.filter(pb => 
    pb.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalContacts = phonebooks.reduce((sum, pb) => sum + (pb.totalContacts || 0), 0);
  const totalOptedIn = phonebooks.reduce((sum, pb) => sum + (pb.optedInContacts || 0), 0);

  if (loading) {
    return (
      <div className="phonebooks-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="phonebooks-page">
      <div className="phonebooks-header">
        <div>
          <h1 className="phonebooks-title">Phonebooks</h1>
          <p className="phonebooks-subtitle">Manage your opted-in contact lists</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="btn-create">
          <Plus size={16} />
          New Phonebook
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="phonebooks-stats">
        <div className="stat-card">
          <div className="stat-icon purple">
            <Users size={20} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{phonebooks.length}</div>
            <div className="stat-label">Phonebooks</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <Phone size={20} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{totalContacts.toLocaleString()}</div>
            <div className="stat-label">Total Contacts</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <CheckCircle size={20} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{totalOptedIn.toLocaleString()}</div>
            <div className="stat-label">Opted-in</div>
          </div>
        </div>
      </div>

      <div className="phonebooks-layout">

        {/* Left Panel - Phonebooks List */}
        <Card className="phonebooks-list" padding="none">
          <div className="list-header">
            <h3 className="list-title">All Phonebooks</h3>
            <div className="list-search">
              <Search size={14} className="list-search-icon" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="list-search-input"
              />
            </div>
          </div>
          <div className="phonebooks-items">
            {filteredPhonebooks.length === 0 ? (
              <div className="empty-list">
                <Users size={40} className="empty-icon" />
                <p>No phonebooks found</p>
                <button className="empty-link" onClick={() => setShowCreateModal(true)}>
                  Create your first phonebook →
                </button>
              </div>
            ) : (
              filteredPhonebooks.map((pb) => (
                <div
                  key={pb.id}
                  className={`phonebook-item ${selectedPhonebook?.id === pb.id ? 'selected' : ''}`}
                  onClick={() => handleSelectPhonebook(pb)}
                >
                  <div className="phonebook-info">
                    <div className="phonebook-name">{pb.name}</div>
                    <div className="phonebook-stats">
                      <span>{pb.totalContacts} contacts</span>
                      <Badge variant="success">{pb.optedInContacts} opted-in</Badge>
                    </div>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={(e) => { e.stopPropagation(); handleDeletePhonebook(pb.id); }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Right Panel - Contacts List */}
        <Card className="contacts-list" padding="none">
          {selectedPhonebook ? (
            <>
              <div className="contacts-header">
                <div>
                  <h3 className="list-title">{selectedPhonebook.name}</h3>
                  <p className="contacts-subtitle">
                    {selectedPhonebook.totalContacts} total contacts • {selectedPhonebook.optedInContacts} opted-in
                  </p>
                </div>
                <div className="contacts-actions">
                  <Button size="sm" variant="outline" onClick={() => { setError(''); setShowUploadModal(true); }}>
                    <Upload size={14} /> Upload CSV
                  </Button>
                  <Button size="sm" onClick={() => { setError(''); setShowAddModal(true); }}>
                    <UserPlus size={14} /> Add Contact
                  </Button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="contacts-table">
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Name (var1)</th>
                      <th>Extra 1 (var2)</th>
                      <th>Extra 2 (var3)</th>
                      <th>Extra 3 (var4)</th>
                      <th>Extra 4 (var5)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr className="empty-row">
                        <td colSpan={7}>
                          <div className="empty-state">
                            <Phone size={32} className="empty-icon" />
                            <p>No contacts yet</p>
                            <div className="empty-actions">
                              <button className="empty-link" onClick={() => setShowUploadModal(true)}>
                                <Upload size={14} /> Upload CSV →
                              </button>
                              <button className="empty-link" onClick={() => setShowAddModal(true)}>
                                <UserPlus size={14} /> Add manually →
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      entries.map((entry) => (
                        <tr key={entry.id}>
                          <td className="phone-number">{entry.phoneNumber}</td>
                          <td>{entry.variable1 || '-'}</td>
                          <td>{entry.variable2 || '-'}</td>
                          <td>{entry.variable3 || '-'}</td>
                          <td>{entry.variable4 || '-'}</td>
                          <td>{entry.variable5 || '-'}</td>
                          <td>
                            {entry.optedIn ? (
                              <Badge variant="success">Opted-in</Badge>
                            ) : (
                              <Badge variant="default">Pending</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <Users size={48} className="no-selection-icon" />
              <h3>Select a phonebook</h3>
              <p>Choose a phonebook from the left to view and manage contacts</p>
            </div>
          )}
        </Card>
      </div>

      {/* ── Create Phonebook Modal ── */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Phonebook">
        <form onSubmit={handleCreate} className="phonebook-form">
          <div className="form-group">
            <label className="form-label">Phonebook Name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Bank Customers, Newsletter Subscribers"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description (optional)</label>
            <textarea
              className="form-textarea"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of this phonebook"
              rows={3}
            />
          </div>
          {error && <div className="alert error">{error}</div>}
          <div className="modal-actions">
            <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>Create Phonebook</Button>
          </div>
        </form>
      </Modal>

      {/* ── Add Contact Modal ── */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Contact">
        <form onSubmit={handleAddNumber} className="phonebook-form">
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <Input
              value={addForm.phoneNumber}
              onChange={(e) => setAddForm({ ...addForm, phoneNumber: e.target.value })}
              placeholder="+255XXXXXXXXX"
              hint="Tanzania format: +255 followed by 9 digits"
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Name (Variable 1)</label>
              <Input
                value={addForm.variable1}
                onChange={(e) => setAddForm({ ...addForm, variable1: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Extra Info 1 (Variable 2)</label>
              <Input
                value={addForm.variable2}
                onChange={(e) => setAddForm({ ...addForm, variable2: e.target.value })}
                placeholder="e.g., Amount, City"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Extra Info 2 (Variable 3)</label>
              <Input
                value={addForm.variable3}
                onChange={(e) => setAddForm({ ...addForm, variable3: e.target.value })}
                placeholder="e.g., Due date, Product"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Extra Info 3 (Variable 4)</label>
              <Input
                value={addForm.variable4}
                onChange={(e) => setAddForm({ ...addForm, variable4: e.target.value })}
                placeholder="e.g., Reference number"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Extra Info 4 (Variable 5)</label>
            <Input
              value={addForm.variable5}
              onChange={(e) => setAddForm({ ...addForm, variable5: e.target.value })}
              placeholder="e.g., Notes"
            />
          </div>

          {error && <div className="alert error">{error}</div>}
          
          <div className="modal-actions">
            <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>Add Contact</Button>
          </div>
        </form>
      </Modal>

      {/* Upload CSV Modal  */}
      <Modal isOpen={showUploadModal} onClose={handleCloseUploadModal} title="Upload CSV">
        <div className="phonebook-form">

          <div className="csv-format-guide">
            <div className="csv-guide-title">
              <FileText size={14} />
              CSV Format Guide
            </div>
            <div className="csv-guide-code">
              <div className="csv-guide-row header-row">phone_number, name, extra1, extra2, extra3, extra4</div>
              <div className="csv-guide-row">+255712345678, John Doe, Dar es Salaam, VIP, REF123, Notes</div>
              <div className="csv-guide-row">+255698765432, Jane Smith, Mwanza, Regular, REF456, </div>
            </div>
            <p className="csv-guide-note">
              First column is required (phone number). Max 6 columns: phone, var1, var2, var3, var4, var5.
              Header row is auto-detected and skipped. All uploaded contacts are marked as opted-in.
            </p>
          </div>

          <div
            className={`csv-dropzone ${uploadFile ? 'has-file' : ''}`}
            onClick={() => document.getElementById('csv-file-input').click()}
          >
            <Upload size={28} className="dropzone-icon" />
            <p className="dropzone-title">
              {uploadFile ? uploadFile.name : 'Click to select CSV file'}
            </p>
            <p className="dropzone-subtitle">
              {uploadFile
                ? `${(uploadFile.size / 1024).toFixed(1)} KB — ready to upload`
                : 'Only .csv files supported'}
            </p>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                setUploadFile(e.target.files[0] || null);
                setError('');
                setUploadResult(null);
              }}
            />
          </div>

          {error && <div className="alert error">{error}</div>}

          {uploadResult && (
            <div className="alert success">
              <CheckCircle size={16} />
              {uploadResult}
            </div>
          )}

          <div className="modal-actions">
            <Button variant="ghost" onClick={handleCloseUploadModal}>
              {uploadResult ? 'Close' : 'Cancel'}
            </Button>
            <Button
              onClick={handleUploadCsv}
              loading={uploading}
              disabled={!uploadFile || uploading}
            >
              <Upload size={14} />
              Import Contacts
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Phonebooks;